import { useCallback, useMemo, useState } from "react";
import {
  buildEditFormState,
  getInitialFormState,
  rebuildFormAfterFrequencyToggle,
  rebuildFormAfterIntensityToggle,
} from "./listeningFormUtils";
import { LISTENING_FORM_STEPS } from "./listeningModes";
import { validateListeningForm } from "./listeningValidation";

export default function useListeningForm({
  normalizedAvailableFrequencies,
  defaultSelectedFrequencies,
  defaultDeviceId,
  defaultModelName,
  isByModel,
}) {
  const buildFreshForm = useCallback(
    () =>
      getInitialFormState({
        availableFrequencies: normalizedAvailableFrequencies,
        defaultSelectedFrequencies,
        defaultDeviceId,
        defaultModelName,
        isByModel,
      }),
    [
      normalizedAvailableFrequencies,
      defaultSelectedFrequencies,
      defaultDeviceId,
      defaultModelName,
      isByModel,
    ],
  );

  const [form, setForm] = useState(buildFreshForm);
  const [step, setStep] = useState(LISTENING_FORM_STEPS.DECLARATION);
  const [validationErrors, setValidationErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const clearValidationState = useCallback(() => {
    setValidationErrors({});
    setFieldErrors({});
    setSubmitError(null);
  }, []);

  const resetCreateState = useCallback(() => {
    setStep(LISTENING_FORM_STEPS.DECLARATION);
    clearValidationState();
    setSubmitting(false);
    setForm(buildFreshForm());
  }, [buildFreshForm, clearValidationState]);

  const resetEditState = useCallback(() => {
    setStep(LISTENING_FORM_STEPS.DECLARATION);
    clearValidationState();
    setSubmitting(false);
  }, [clearValidationState]);

  const loadEditForm = useCallback(
    (detailedListening) => {
      if (!detailedListening) {
        return;
      }

      resetEditState();
      setForm(buildEditFormState(detailedListening));
    },
    [resetEditState],
  );

  const runValidation = useCallback((nextForm, byModelFlag) => {
    const result = validateListeningForm({
      form: nextForm,
      isByModel: byModelFlag,
      allowedFrequencies: normalizedAvailableFrequencies,
    });

    setValidationErrors(result.errors);
    setFieldErrors(result.fieldErrors);

    return result;
  }, [normalizedAvailableFrequencies]);

  const validateDeclarationStep = useCallback((nextForm, byModelFlag) => {
    const result = validateListeningForm({
      form: nextForm,
      isByModel: byModelFlag,
      allowedFrequencies: normalizedAvailableFrequencies,
    });

    const declarationErrors = {};
    [
      "description",
      "location",
      "deviceId",
      "selectedFrequencies",
      "selectedIntensities",
    ].forEach((key) => {
      if (result.errors[key]) {
        declarationErrors[key] = result.errors[key];
      }
    });

    setValidationErrors(declarationErrors);
    setFieldErrors({});

    return Object.keys(declarationErrors).length === 0;
  }, [normalizedAvailableFrequencies]);

  const handleFieldChange = useCallback((fieldName, value) => {
    setForm((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    setValidationErrors((prev) => {
      if (!prev[fieldName]) {
        return prev;
      }

      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const toggleFrequency = useCallback((frequency) => {
    setForm((prev) => rebuildFormAfterFrequencyToggle(prev, frequency));

    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.selectedFrequencies;
      delete next.noStimulusByFrequency;
      delete next.tableValues;
      return next;
    });

    setFieldErrors({});
  }, []);

  const toggleIntensity = useCallback((intensity) => {
    setForm((prev) => rebuildFormAfterIntensityToggle(prev, intensity));

    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.selectedIntensities;
      delete next.tableValues;
      return next;
    });

    setFieldErrors({});
  }, []);

  const handleNoStimulusChange = useCallback((frequency, value) => {
    setForm((prev) => ({
      ...prev,
      noStimulusByFrequency: {
        ...prev.noStimulusByFrequency,
        [frequency]: value,
      },
    }));

    setValidationErrors((prev) => {
      if (!prev.noStimulusByFrequency) {
        return prev;
      }

      const next = { ...prev };
      delete next.noStimulusByFrequency;
      return next;
    });

    setFieldErrors((prev) => {
      const key = `noStimulusByFrequency.${frequency}`;
      if (!prev[key]) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleTableValueChange = useCallback((frequency, intensity, value) => {
    setForm((prev) => ({
      ...prev,
      tableValues: {
        ...prev.tableValues,
        [frequency]: {
          ...prev.tableValues[frequency],
          [intensity]: value,
        },
      },
    }));

    setValidationErrors((prev) => {
      if (!prev.tableValues) {
        return prev;
      }

      const next = { ...prev };
      delete next.tableValues;
      return next;
    });

    setFieldErrors((prev) => {
      const key = `tableValues.${frequency}.${intensity}`;
      if (!prev[key]) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const goToValuesStep = useCallback(
    (byModelFlag) => {
      if (!validateDeclarationStep(form, byModelFlag)) {
        return false;
      }

      setStep(LISTENING_FORM_STEPS.VALUES);
      setSubmitError(null);
      setFieldErrors({});
      return true;
    },
    [form, validateDeclarationStep],
  );

  const goBackToDeclarationStep = useCallback(() => {
    setStep(LISTENING_FORM_STEPS.DECLARATION);
    clearValidationState();
  }, [clearValidationState]);

  const editAvailableFrequencies = useMemo(() => {
    const detailedFrequencies = form?.frequencies || [];

    return [
      ...new Set([...normalizedAvailableFrequencies, ...detailedFrequencies]),
    ]
      .map(Number)
      .filter((value) => !Number.isNaN(value))
      .sort((a, b) => a - b);
  }, [form, normalizedAvailableFrequencies]);

  return {
    form,
    setForm,
    step,
    validationErrors,
    fieldErrors,
    submitError,
    setSubmitError,
    submitting,
    setSubmitting,

    resetCreateState,
    resetEditState,
    loadEditForm,
    runValidation,

    handleFieldChange,
    toggleFrequency,
    toggleIntensity,
    handleNoStimulusChange,
    handleTableValueChange,

    goToValuesStep,
    goBackToDeclarationStep,
    editAvailableFrequencies,
  };
}
